import { FC, Fragment, useMemo } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Radio,
  Typography,
} from "@mui/material";
import { CommonStyle, ControlledForm } from "Components";
import {
  CPQuesBoxProps,
  CPQuesOptType,
} from "DataTypes/PatientManagement.type";
import ControlledAccord from "Components/Accordion1/Controlled";
import ImgPath from "Constant/Imgs";
import NoData from "Components/NoData";
import PatientFormatter from "Utils/Patient.Formatter";
import Stages from "Constant/Stages";
import TextEditor from "Components/TextEditor";
import { label } from "Constant/En";

const QuestionBox: FC<CPQuesBoxProps> = (props) => {
  const {
    catQuesOpt,
    handleQuestionClick,
    openQuestionIds,
    handleOptionClick,
    handleChangeText,
    handleUpdateText,
    handleSave,
    loadingOptQuesId,
    setLocalInput,
    localInput,
    readOnly,
    selectedPrgm,
    pressedNext,
    selectedQuesId,
    isRecurring
  } = props;

  const MainContent: (mainQues: CPQuesOptType) => React.ReactElement = (mainQues) => {
    return (
      <Box display={"flex"}>
        <Typography variant="subtitle1" sx={{ wordBreak: "break-all" }}>
          {mainQues.question}
        </Typography>
        {mainQues.requiredQuestion && (
          <Typography variant="subtitle1" color="red">
            *
          </Typography>
        )}
      </Box>
    )
  };

  const SubContent: (
    subQues: CPQuesOptType, isBGBlue: boolean
  ) => React.ReactElement = (subQues, isBGBlue) => {
    return (
      <Fragment>
        {subQues.questionType == "singleSelect" ||
          subQues.questionType == "multiSelect" ? (
            <Fragment>
              {loadingOptQuesId == subQues.questionsId ? (
                <CircularProgress size={20} />
              ) : (
                subQues.options.length > 0 && (
                  <Fragment>
                    {subQues.options.map((optEl, index) => {
                      let filteredArr: CPQuesOptType[] = [];
                      if (catQuesOpt) {
                        filteredArr = catQuesOpt.questionList.filter(
                          (quesEl) => quesEl.parentOptionId == optEl.id
                        );
                      }
                      return (
                        <Fragment key={`${optEl.id} ${index}`}>
                          <Box
                            key={`${optEl.id} ${index}`}
                            {...(!readOnly && {
                              onClick: () => {
                                handleOptionClick(subQues, optEl.id)
                              },
                            })}
                            sx={{
                              cursor: readOnly ? "default" : "pointer",
                              ...CommonStyle.sxRow,
                            }}
                          >
                            {subQues.questionType == "multiSelect" && (
                              <Checkbox
                                checked={optEl.answered}
                                color="secondary"
                                sx={{
                                  "& .MuiSvgIcon-root": {
                                    fontSize: "1rem",
                                  },
                                }}
                                disabled={readOnly}
                              />
                            )}
                            {subQues.questionType == "singleSelect" && (
                              <Radio
                                checked={optEl.answered}
                                color="secondary"
                                sx={{
                                  "& .MuiSvgIcon-root": {
                                    fontSize: "1rem",
                                  },
                                }}
                                disabled={readOnly}
                              />
                            )}
                            <Box sx={{ ...CommonStyle.sxRow }}>
                              <Typography
                                variant="subtitle1"
                                sx={{ wordBreak: "break-all" }}
                              >
                                {optEl.option}
                              </Typography>
                              {optEl.requiredOption && (
                                <Typography variant="subtitle1" color="red">
                                *
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          {filteredArr.length > 0 &&
                          optEl.answered &&
                          filteredArr.map((filEl) => {
                            const find = openQuestionIds.find(
                              (openQuesEl) => openQuesEl == filEl.questionsId
                            );
                            return (
                              <ControlledAccord
                                key={filEl.questionsId}
                                open={find ? true : false}
                                head={MainContent(filEl)}
                                body={
                                  find ? SubContent(filEl, !isBGBlue) : <></>
                                }
                                {...(pressedNext &&
                                  filEl.requiredQuestion &&
                                  (!filEl.isAnswered ||
                                    !filEl.isRequiredOptionAnswered) && {
                                  arrowColor: "red",
                                })}
                                {...(filEl.isAnswered &&
                                  filEl.requiredQuestion &&
                                  !filEl.isRequiredOptionAnswered && {
                                  arrowColor: "red",
                                })}
                                {...(filEl.isAnswered &&
                                  filEl.requiredQuestion &&
                                  filEl.isRequiredOptionAnswered && {
                                  arrowColor: "green",
                                })}
                                {...(filEl.isAnswered &&
                                  !filEl.requiredQuestion && {
                                  arrowColor: "green",
                                })}
                                handleClick={() => handleQuestionClick(filEl)}
                                roundIcon={true}
                                style={{
                                  ...(selectedQuesId == filEl.questionsId ? {
                                    "&>div:last-of-type": {
                                      backgroundColor: "#bfdff3ff !important"
                                    }
                                  } : {})
                                }}
                              />
                            );
                          })}
                        </Fragment>
                      );
                    })}
                  </Fragment>
                )
              )}
            </Fragment>
          ) : (
            <Box width="100%">
              <ControlledForm.Input
                id="FreeTextBox"
                placeholder="Enter Answer"
                value={subQues.freeTextAnswer}
                onChange={(el) =>
                  handleChangeText(
                    subQues.questionsId,
                    typeof el == "string" ? el : "",
                    "answer"
                  )
                }
                onBlurChange={() => {
                  subQues.freeTextAnswer != localInput &&
                  handleUpdateText(
                    subQues,
                    JSON.parse(JSON.stringify(localInput)),
                    "answer"
                  );
                }}
                onFocusChange={() => {
                  setLocalInput(subQues.freeTextAnswer, subQues.questionsId)
                }}
                readOnly={readOnly}
                multiline
              />
            </Box>
          )}
        <Box width="100%" mt={1}>
          <TextEditor
            onBlur={(el) => {
              el != localInput &&
                handleUpdateText(
                  { ...subQues, questionComment: el },
                  JSON.parse(JSON.stringify(localInput)),
                  "comment"
                );
            }}
            onFocus={() => {
              setLocalInput(subQues.questionComment, subQues.questionsId)
            }}
            placeholder="Enter Comments"
            value={subQues.questionComment}
            readOnly={readOnly}
          />
        </Box>
        {subQues.isAnswered && isRecurring &&
          <Typography variant="subtitle1" textAlign={"end"}>
            {`
          ${selectedPrgm?.careplanActivity} 
          performed and documented by 
          ${subQues.updatedBy || label.naText} on 
          ${subQues.updatedAt || label.naText}
          `}
          </Typography>
        }
      </Fragment>
    )
  };

  const NullFilterArr: CPQuesOptType[] = catQuesOpt
    ? catQuesOpt.questionList.filter(
      (el) => el.parentOptionId == null && el.parentQuestionId == null
    )
    : [];

  return useMemo(() => {
    return (
      <Box width="66%" height="100%">
        {catQuesOpt ? (
          <Box height="100%" sx={{ ...CommonStyle.sxWhiteBox }}>
            <Box
              sx={{
                ...CommonStyle.sxRow,
                ...sxQuestionHead,
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ ...CommonStyle.sxRow }}
              >
                {`Questions > ${catQuesOpt.category}`}
              </Typography>
              {readOnly ? (
                (selectedPrgm?.careplanStage || 0) >
                PatientFormatter.GetCPStage(Stages.CPQues) && (
                  <Box
                    onClick={handleSave}
                    sx={{
                      ...CommonStyle.sxRoundIconsMainSm,
                      mr: 1,
                      p: 0.1,
                      "&>svg": {
                        rotate: "180deg",
                      },
                    }}
                  >
                    <ImgPath.BreadcrumbIcon
                      titleAccess="Next"
                      sx={{ width: "0.9rem" }}
                    />
                  </Box>
                )
              ) : (
                <Box
                  onClick={handleSave}
                  sx={{
                    ...CommonStyle.sxRoundIconsMainSm,
                    mr: 1,
                    p: 0.1,
                    "&>svg": {
                      rotate: "180deg",
                    },
                  }}
                >
                  <ImgPath.BreadcrumbIcon
                    titleAccess="Save & Next"
                    sx={{ width: "0.9rem" }}
                  />
                </Box>
              )}
            </Box>

            <Box height="90%" overflow="auto" sx={{ px: 2 }}>
              {NullFilterArr.length > 0 ? (
                NullFilterArr.map((el) => {
                  const find = openQuestionIds.find(
                    (openQuesEl) => openQuesEl == el.questionsId
                  );
                  return (
                    <ControlledAccord
                      key={el.questionsId}
                      open={find ? true : false}
                      head={MainContent(el)}
                      body={find ? SubContent(el, true) : <></>}
                      {...(pressedNext &&
                        el.requiredQuestion &&
                        (!el.isAnswered || !el.isRequiredOptionAnswered) && {
                        arrowColor: "red",
                      })}
                      {...(el.isAnswered &&
                        el.requiredQuestion &&
                        !el.isRequiredOptionAnswered && { arrowColor: "red" })}
                      {...(el.isAnswered &&
                        el.requiredQuestion &&
                        el.isRequiredOptionAnswered && { arrowColor: "green" })}
                      {...(el.isAnswered &&
                        !el.requiredQuestion && { arrowColor: "green" })}
                      handleClick={() => handleQuestionClick(el)}
                      roundIcon={true}
                      style={{
                        ...(selectedQuesId == el.questionsId ? {
                          "&>div:last-of-type": {
                            backgroundColor: "#bfdff3ff !important"
                          }
                        } : {})
                      }}
                    />
                  );
                })
              ) : (
                <NoData />
              )}
            </Box>
          </Box>
        ) : (
          <NoData />
        )}
      </Box>
    );
  }, [catQuesOpt, openQuestionIds, selectedQuesId]);
};
export default QuestionBox;

const sxQuestionHead = {
  height: "10%",
  px: 2,
};
